<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { del, get, post, put } from '@/api'

definePage({
  meta: {
    title: 'CDN 캠페인 URL',
    action: 'read',
    subject: 'Campaign',
  },
})

// 캠페인 인터페이스
interface CdnCampaign {
  id: number | null
  name: string | null
  fb_id: string | null
  redirect: string | null
  evt: string[]
  content_type: string
  value: string | null
  currency: string
  content_ids: string[]
  description: string | null
}

// 초기 캠페인 데이터
const getInitialCampaign = (): CdnCampaign => ({
  id: null,
  name: null,
  fb_id: null,
  redirect: null,
  evt: ['ViewContent'],
  content_type: 'product',
  value: null,
  currency: 'KRW',
  content_ids: [],
  description: null,
})

const { snackbar, handleError } = useErrorHandler()

// 상태
const campaign = reactive<CdnCampaign>(getInitialCampaign())
const campaigns = ref<CdnCampaign[]>([])
const loading = ref(false)
const saving = ref(false)
const selectedCampaignId = ref<number | null>(null)

// 상품코드 입력 관련
const newContentId = ref('')

// 이벤트 타입 옵션
const eventTypeOptions = [
  'ViewContent',
  'AddToCart',
  'AddToWishlist',
  'InitiateCheckout',
  'AddPaymentInfo',
  'Purchase',
  'CompleteRegistration',
  'Lead',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
]

// 통화 옵션
const currencyOptions = [
  'USD',
  'KRW',
  'EUR',
  'JPY',
  'GBP',
  'CNY',
  'AUD',
  'CAD',
  'CHF',
  'HKD',
  'SGD',
  'THB',
  'TWD',
  'VND',
  'PHP',
  'MYR',
  'IDR',
  'INR',
  'BRL',
  'MXN',
]

// 테이블 헤더
const headers = [
  { title: '', key: 'selected', width: '50px', sortable: false },
  { title: '#', key: 'index', width: '60px', sortable: false },
  { title: '캠페인명', key: 'name' },
  { title: '랜딩 페이지', key: 'redirect' },
  { title: '비고', key: 'description' },
]

// URL 프리뷰
const cdnUrlPrefix = 'https://global.svcdn.one/?'

const cdnCampaignUrl = computed(() => {
  if (!campaign.name)
    return ''

  const params: string[] = []

  if (campaign.id)
    params.push(`id=${campaign.id}`)
  if (campaign.name)
    params.push(`name=${campaign.name}`)
  if (campaign.fb_id)
    params.push(`fb_id=${campaign.fb_id}`)
  if (campaign.redirect)
    params.push(`redirect=${encodeURIComponent(campaign.redirect)}`)

  campaign.evt.forEach(evt => {
    params.push(`evt[]=${evt}`)
  })

  if (campaign.content_type)
    params.push(`content_type=${campaign.content_type}`)
  if (campaign.value)
    params.push(`value=${campaign.value}`)
  if (campaign.currency)
    params.push(`currency=${campaign.currency}`)

  campaign.content_ids.forEach(id => {
    params.push(`content_ids[]=${id}`)
  })

  if (campaign.description)
    params.push(`description=${campaign.description}`)

  return cdnUrlPrefix + params.join('&')
})

// 캠페인 목록 로드
const loadCampaigns = async () => {
  loading.value = true
  try {
    const { success, data } = await get<CdnCampaign[]>('/cdn-campaign')
    if (success && data)
      campaigns.value = Array.isArray(data) ? data : []
  }
  catch (error) {
    handleError(error, { notificationMessage: '캠페인 목록을 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 캠페인 선택
const selectCampaign = (item: CdnCampaign) => {
  if (selectedCampaignId.value === item.id) {
    // 이미 선택된 항목 클릭시 선택 해제
    resetForm()
  }
  else {
    selectedCampaignId.value = item.id
    Object.assign(campaign, JSON.parse(JSON.stringify(item)))
  }
}

// 캠페인 저장
const storeCampaign = async () => {
  if (!campaign.name) {
    snackbar.warning('캠페인명을 입력해주세요.')

    return
  }

  saving.value = true
  try {
    const { success, error } = await post('/cdn-campaign', campaign)
    if (success) {
      snackbar.success('캠페인이 등록되었습니다.')
      await loadCampaigns()
      resetForm()
    }
    else if (error) {
      handleError(error, { notificationMessage: '등록에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '등록에 실패했습니다.' })
  }
  finally {
    saving.value = false
  }
}

// 캠페인 수정
const updateCampaign = async () => {
  if (!campaign.id)
    return

  saving.value = true
  try {
    const { success, error } = await put(`/cdn-campaign/${campaign.id}`, campaign)
    if (success) {
      snackbar.success('캠페인이 수정되었습니다.')
      await loadCampaigns()
    }
    else if (error) {
      handleError(error, { notificationMessage: '수정에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '수정에 실패했습니다.' })
  }
  finally {
    saving.value = false
  }
}

// 캠페인 삭제
const deleteCampaign = async () => {
  if (!campaign.id)
    return

  if (!confirm('캠페인을 삭제하시겠습니까?'))
    return

  saving.value = true
  try {
    const { success, error } = await del(`/cdn-campaign/${campaign.id}`)
    if (success) {
      snackbar.success('캠페인이 삭제되었습니다.')
      await loadCampaigns()
      resetForm()
    }
    else if (error) {
      handleError(error, { notificationMessage: '삭제에 실패했습니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '삭제에 실패했습니다.' })
  }
  finally {
    saving.value = false
  }
}

// 폼 초기화
const resetForm = () => {
  Object.assign(campaign, getInitialCampaign())
  selectedCampaignId.value = null
}

// 상품코드 추가
const addContentId = () => {
  if (newContentId.value && !campaign.content_ids.includes(newContentId.value)) {
    campaign.content_ids.push(newContentId.value)
    newContentId.value = ''
  }
}

// 상품코드 삭제
const removeContentId = (index: number) => {
  campaign.content_ids.splice(index, 1)
}

// URL 복사
const copyUrl = async () => {
  if (!cdnCampaignUrl.value)
    return

  try {
    await navigator.clipboard.writeText(cdnCampaignUrl.value)
    snackbar.success('URL이 클립보드에 복사되었습니다.')
  }
  catch {
    snackbar.error('복사에 실패했습니다.')
  }
}

// 초기 로드
onMounted(() => {
  loadCampaigns()
})
</script>

<template>
  <VRow>
    <!-- 캠페인 편집기 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>캠페인 주소 편집기</VCardTitle>
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="campaign.name"
                label="캠페인 명칭"
                placeholder="캠페인명을 입력해주세요"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="campaign.fb_id"
                label="Facebook 픽셀 아이디"
                placeholder="페이스북 픽셀 코드를 입력해주세요"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="campaign.redirect"
                label="랜딩 페이지"
                placeholder="랜딩페이지 주소를 입력해주세요"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol cols="12">
              <div class="text-subtitle-2 mb-2">
                이벤트 타입
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VCheckbox
                  v-for="evt in eventTypeOptions"
                  :key="evt"
                  v-model="campaign.evt"
                  :label="evt"
                  :value="evt"
                  density="compact"
                  hide-details
                />
              </div>
            </VCol>
          </VRow>

          <VRow class="mt-4">
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="campaign.content_type"
                label="콘텐츠 타입"
                placeholder="기본값: product"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="campaign.currency"
                :items="currencyOptions"
                label="통화 단위"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="campaign.value"
                label="금액"
                placeholder="상품가액을 입력해주세요"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol cols="12">
              <div class="text-subtitle-2 mb-2">
                상품코드
              </div>
              <div class="d-flex gap-2 mb-2">
                <VTextField
                  v-model="newContentId"
                  label="상품 코드 입력"
                  placeholder="상품 코드를 입력하고 추가 버튼을 누르세요"
                  density="compact"
                  hide-details
                  @keyup.enter="addContentId"
                />
                <VBtn
                  color="primary"
                  @click="addContentId"
                >
                  추가
                </VBtn>
              </div>
              <div class="d-flex flex-wrap gap-1">
                <VChip
                  v-for="(id, index) in campaign.content_ids"
                  :key="index"
                  closable
                  @click:close="removeContentId(index)"
                >
                  {{ id }}
                </VChip>
              </div>
            </VCol>
          </VRow>

          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="campaign.description"
                label="비고"
                placeholder="추가설명이 필요하면 입력해주세요"
              />
            </VCol>
          </VRow>

          <VRow class="mt-4">
            <VCol>
              <template v-if="campaign.id">
                <VBtn
                  color="primary"
                  :loading="saving"
                  class="me-2"
                  @click="updateCampaign"
                >
                  수정하기
                </VBtn>
                <VBtn
                  color="error"
                  :loading="saving"
                  class="me-2"
                  @click="deleteCampaign"
                >
                  삭제하기
                </VBtn>
                <VBtn
                  variant="outlined"
                  @click="resetForm"
                >
                  새로 작성
                </VBtn>
              </template>
              <template v-else>
                <VBtn
                  color="primary"
                  :loading="saving"
                  class="me-2"
                  @click="storeCampaign"
                >
                  등록하기
                </VBtn>
                <VBtn
                  variant="outlined"
                  @click="resetForm"
                >
                  초기화
                </VBtn>
              </template>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- URL 미리보기 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>만들어진 URL 미리보기</VCardTitle>
        <VCardText>
          <VTextarea
            :model-value="cdnCampaignUrl"
            readonly
            rows="3"
            class="mb-2"
          />
          <VBtn
            color="success"
            :disabled="!cdnCampaignUrl"
            @click="copyUrl"
          >
            복사하기
          </VBtn>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 캠페인 목록 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>캠페인 주소 목록</span>
          <VBtn
            icon="tabler-refresh"
            variant="text"
            :loading="loading"
            @click="loadCampaigns"
          />
        </VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headers"
            :items="campaigns"
            :loading="loading"
            hover
            density="compact"
            class="cursor-pointer"
            @click:row="(_event: Event, { item }: { item: CdnCampaign }) => selectCampaign(item)"
          >
            <template #item.selected="{ item }">
              <VIcon
                v-if="selectedCampaignId === item.id"
                icon="tabler-check"
                color="primary"
              />
            </template>

            <template #item.index="{ index }">
              {{ index + 1 }}
            </template>

            <template #item.name="{ item }">
              {{ item.name || '-' }}
            </template>

            <template #item.redirect="{ item }">
              <span
                class="text-truncate d-inline-block"
                style="max-width: 300px;"
              >
                {{ item.redirect || '-' }}
              </span>
            </template>

            <template #item.description="{ item }">
              {{ item.description || '-' }}
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.cursor-pointer :deep(tbody tr) {
  cursor: pointer;
}
</style>
